import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Spinner, Btn } from "@/components/kit";
import { RelayMark } from "@/components/brand";
import { APP_BUILD } from "@/lib/vda";

import Landing from "@/pages/Landing";
import { OfflineBanner, PwaUpdateWatcher, UatBadge } from "@/components/pwa";
import { SiteGate } from "@/components/SiteGate";
import DriverAuth from "@/pages/driver/DriverAuth";
import DriverHome from "@/pages/driver/DriverHome";
import DriverVehicle from "@/pages/driver/DriverVehicle";
import DriverBook from "@/pages/driver/DriverBook";
import DriverMileageIntake from "@/pages/driver/DriverMileageIntake";
import DriverTyres from "@/pages/driver/DriverTyres";
import DriverGlass from "@/pages/driver/DriverGlass";
import DriverBodywork from "@/pages/driver/DriverBodywork";
import DriverRequest from "@/pages/driver/DriverRequest";
import DriverBreakdown from "@/pages/driver/DriverBreakdown";
import DriverVehicleCheck from "@/pages/driver/DriverVehicleCheck";
import DriverAccident from "@/pages/driver/DriverAccident";
import DriverHistory from "@/pages/driver/DriverHistory";
import DriverBookingDetail from "@/pages/driver/DriverBookingDetail";
import DriverAllocationIssue from "@/pages/driver/DriverAllocationIssue";
import DriverAllocationResolve from "@/pages/driver/DriverAllocationResolve";
import StaffVehicle from "@/pages/staff/StaffVehicle";
import CustomerConfig from "@/pages/staff/CustomerConfig";
import Ingestion from "@/pages/staff/Ingestion";
import AMView from "@/pages/staff/AMView";
import ComplianceRecords from "@/pages/staff/ComplianceRecords";
import OperationalImports from "@/pages/staff/OperationalImports";
import AccidentReportView from "@/pages/staff/AccidentReportView";

import StaffLogin from "@/pages/staff/StaffLogin";
import AEDashboard from "@/pages/staff/AEDashboard";
import StaffBookingDetail from "@/pages/staff/StaffBookingDetail";
import BulkUpdate from "@/pages/staff/BulkUpdate";
import Imports from "@/pages/staff/Imports";
import Oversight from "@/pages/staff/Oversight";
import AllocationHistory from "@/pages/staff/AllocationHistory";
import BusinessCalendars from "@/pages/staff/BusinessCalendars";
import LoginAudit from "@/pages/staff/LoginAudit";
import Notifications from "@/pages/staff/Notifications";
import Confirmations from "@/pages/staff/Confirmations";
import ConfirmationAudit from "@/pages/staff/ConfirmationAudit";
import OtherPods from "@/pages/staff/OtherPods";
import ClientVehicle from "@/pages/external/ClientVehicle";
import FleetLayout, {
  FleetDashboardPage, FleetUrgentPage, FleetVehiclesPage, FleetRequestsPage, FleetChecksPage,
  FleetServiceMotPage, FleetCompliancePage, FleetRecallsPage, FleetContractsPage,
} from "@/pages/external/FleetWorkspace";

const STAFF_ROLES = ["account_executive", "senior_account_executive", "account_manager", "pod_lead", "administrator"];

function homeFor(role) {
  if (role === "driver") return "/driver";
  if (role === "external_fleet_manager") return "/fleet";
  if (STAFF_ROLES.includes(role)) return "/staff";
  return "/";
}

function Guard({ allow, children }) {
  const { loading, session, role } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  const entry = allow.includes("driver") ? "/verify" : "/login";
  if (!session || !role) return <Navigate to={entry} state={{ from: loc }} replace />;
  const ok = allow.some((a) => (a === "staff" ? STAFF_ROLES.includes(role) : role === a));
  if (!ok) return <Navigate to={homeFor(role)} replace />;
  return children;
}

function RootRedirect() {
  const { loading, session, role } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
  // Only redirect when we have a resolved role; a session without a role means
  // an unusable auth state — show the entry point rather than looping on "/".
  if (session && role) return <Navigate to={homeFor(role)} replace />;
  return <Landing />;
}

// Restrained loading state that can NEVER spin forever — after a grace period it
// offers a recovery action instead of an endless spinner.
function LoadingScreen() {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSlow(true), 12000); return () => clearTimeout(t); }, []);
  if (slow) return <RecoveryScreen reason="slow" />;
  return <div className="min-h-screen grid place-items-center"><Spinner /></div>;
}

function RecoveryScreen({ reason }) {
  const { retry, signOut } = useAuth();
  return (
    <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="boot-recovery">
      <div className="w-full max-w-sm">
        <div className="mb-6"><RelayMark size="text-2xl" className="text-primary" /></div>
        <h1 className="text-xl font-semibold tracking-tight">relay. couldn't finish loading.</h1>
        <p className="text-sm text-muted-foreground mt-2">
          {reason === "slow"
            ? "This is taking longer than expected. Please try again."
            : "We couldn't restore your session. Try again, or start over to sign in fresh."}
        </p>
        <div className="mt-6 space-y-2">
          <Btn className="w-full" data-testid="boot-retry" onClick={() => retry()}>
            <RefreshCw className="h-4 w-4" /> Try again
          </Btn>
          <Btn variant="outline" className="w-full" data-testid="boot-startover"
            onClick={async () => { await signOut(); window.location.assign("/"); }}>
            Start over
          </Btn>
        </div>
        <p className="mt-6 text-[11px] font-mono text-muted-foreground/70">relay. {APP_BUILD}</p>
      </div>
    </div>
  );
}

// Global bootstrap gate: guarantees every startup path terminates in a rendered
// screen (app / auth / recovery) and never an indefinite spinner.
function BootGate({ children }) {
  const { authError, loading } = useAuth();
  if (authError) return <RecoveryScreen reason="auth" />;
  if (loading) return <LoadingScreen />;
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <SiteGate>
      <OfflineBanner />
      <PwaUpdateWatcher />
      <UatBadge />
      <BootGate>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/verify" element={<DriverAuth />} />
          <Route path="/login" element={<StaffLogin />} />

          {/* Driver */}
          <Route path="/driver" element={<Guard allow={["driver"]}><DriverHome /></Guard>} />
          <Route path="/driver/vehicle" element={<Guard allow={["driver"]}><DriverVehicle /></Guard>} />
          <Route path="/driver/book" element={<Guard allow={["driver"]}><DriverBook /></Guard>} />
          <Route path="/driver/mileage" element={<Guard allow={["driver"]}><DriverMileageIntake /></Guard>} />
          <Route path="/driver/tyres" element={<Guard allow={["driver"]}><DriverTyres /></Guard>} />
          <Route path="/driver/glass" element={<Guard allow={["driver"]}><DriverGlass /></Guard>} />
          <Route path="/driver/bodywork" element={<Guard allow={["driver"]}><DriverBodywork /></Guard>} />
          <Route path="/driver/request" element={<Guard allow={["driver"]}><DriverRequest /></Guard>} />
          <Route path="/driver/breakdown" element={<Guard allow={["driver"]}><DriverBreakdown /></Guard>} />
          <Route path="/driver/vehicle-check" element={<Guard allow={["driver"]}><DriverVehicleCheck /></Guard>} />
          <Route path="/driver/accident" element={<Guard allow={["driver"]}><DriverAccident /></Guard>} />
          <Route path="/driver/history" element={<Guard allow={["driver"]}><DriverHistory /></Guard>} />
          <Route path="/driver/booking/:id" element={<Guard allow={["driver"]}><DriverBookingDetail /></Guard>} />
          <Route path="/driver/allocation-issue" element={<Guard allow={["driver"]}><DriverAllocationIssue /></Guard>} />
          <Route path="/driver/allocation-resolve" element={<Guard allow={["driver"]}><DriverAllocationResolve /></Guard>} />
          {/* Legacy fault route → merged into the new taxonomy */}
          <Route path="/driver/fault" element={<Guard allow={["driver"]}><DriverBook /></Guard>} />

          {/* Staff */}
          <Route path="/staff" element={<Guard allow={["staff"]}><AEDashboard /></Guard>} />
          <Route path="/staff/booking/:id" element={<Guard allow={["staff"]}><StaffBookingDetail /></Guard>} />
          <Route path="/staff/vehicle/:id" element={<Guard allow={["staff"]}><StaffVehicle /></Guard>} />
          <Route path="/staff/bulk" element={<Guard allow={["staff"]}><BulkUpdate /></Guard>} />
          <Route path="/staff/imports" element={<Guard allow={["staff"]}><Imports /></Guard>} />
          <Route path="/staff/allocations" element={<Guard allow={["staff"]}><AllocationHistory /></Guard>} />
          <Route path="/staff/oversight" element={<Guard allow={["staff"]}><Oversight /></Guard>} />
          <Route path="/staff/notifications" element={<Guard allow={["staff"]}><Notifications /></Guard>} />
          <Route path="/staff/confirmations/audit" element={<Guard allow={["staff"]}><ConfirmationAudit /></Guard>} />
          <Route path="/staff/confirmations" element={<Guard allow={["staff"]}><Confirmations /></Guard>} />
          <Route path="/staff/ingestion" element={<Guard allow={["staff"]}><Ingestion /></Guard>} />
          <Route path="/staff/other-pods" element={<Guard allow={["staff"]}><OtherPods /></Guard>} />
          <Route path="/staff/am-view" element={<Guard allow={["staff"]}><AMView /></Guard>} />
          <Route path="/staff/compliance" element={<Guard allow={["staff"]}><ComplianceRecords /></Guard>} />
          <Route path="/staff/operational-imports" element={<Guard allow={["staff"]}><OperationalImports /></Guard>} />
          <Route path="/staff/accident/:id" element={<Guard allow={["staff", "external_fleet_manager"]}><AccidentReportView /></Guard>} />
          <Route path="/staff/customer-config" element={<Guard allow={["administrator"]}><CustomerConfig /></Guard>} />
          <Route path="/staff/calendars" element={<Guard allow={["administrator"]}><BusinessCalendars /></Guard>} />
          <Route path="/staff/security" element={<Guard allow={["administrator"]}><LoginAudit /></Guard>} />

          {/* External — Fleet Manager workspace (config-driven app shell + focused views) */}
          <Route path="/fleet" element={<Guard allow={["external_fleet_manager"]}><FleetLayout /></Guard>}>
            <Route index element={<FleetDashboardPage />} />
            <Route path="urgent" element={<FleetUrgentPage />} />
            <Route path="vehicles" element={<FleetVehiclesPage />} />
            <Route path="requests" element={<FleetRequestsPage />} />
            <Route path="checks" element={<FleetChecksPage />} />
            <Route path="service-mot" element={<FleetServiceMotPage />} />
            <Route path="compliance" element={<FleetCompliancePage />} />
            <Route path="recalls" element={<FleetRecallsPage />} />
            <Route path="contracts" element={<FleetContractsPage />} />
            <Route path="vehicle/:id" element={<ClientVehicle />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BootGate>
      </SiteGate>
    </BrowserRouter>
  );
}
